import { SmartInput } from "../reusable-elements/floating-smart-input";
import { useState, forwardRef, useImperativeHandle } from "react";
import { notNullOrEmpty, validateField } from "../../utils/validators";
import { DocPreview } from "./doc-preview";
import { RightAlignedButtons } from './../reusable-elements/right-aligned-buttons';
import { DocTypeSelector } from "../reusable-elements/doctype-selector";
import Comparer from "../../app/comparer";
import DateTimePicker from "../calendar/datetime-picker";
import DateHelpers from '../../app/datehelpers';
import moment from 'moment';

import './../company/card-styles.css';

export const DocTile = forwardRef(({ doc, docTypes, onStateChange, isSaving, showDelete }, ref) => {

    const comparer = new Comparer();

    const [state, setState] = useState(doc);
    const [originalState, setOriginalState] = useState(structuredClone(doc));
    const [validationErrors, setValidationErrors] = useState(null);
    const [rerender, setRerender] = useState(false);
    const [openDtp, setOpenDtp] = useState(false);

    const dateTimeHelper = new DateHelpers();

    // Expose methods to the parent component
    useImperativeHandle(ref, () => ({
        reset: () => {
            setState(doc);
            setOriginalState(structuredClone(doc));
            setOpenDtp(false);
        }
    }));

    const validateState = stateToValidate => {
        validateField(() => notNullOrEmpty(stateToValidate.title), () => stateToValidate.title, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.description), () => stateToValidate.description, setValidationErrors);
    };

    const handleStateUpdates = updateMethod => {

        setState(current => {
            const newState = updateMethod(current);
            validateState(newState);
            onStateChange(newState);
            setRerender(!rerender);
            return newState;
        });
    };

    const hasChanges = () => {
        const equal = comparer.equals(state, originalState);
        return !equal;
    };

    const handleCancel = () => {
        setState(current => {
            onStateChange(structuredClone(originalState));
            setRerender(!rerender);
            return structuredClone(originalState);
        });

        setOpenDtp(false);
    };

    const getDocType = newDocType => originalState.documentType.id === newDocType.id ? originalState.documentType : newDocType;

    const convertDateFromMomentToISO = m => {
        const endOfDay = dateTimeHelper.moveMomentToEndOfDay(m);
        return endOfDay.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
    };

    const daysUntilExpiry = () => {

        const expiry = dateTimeHelper.convertDateStringToMoment(state.expiry).local();
        var now = moment();

        if (expiry.diff(now, "hours") < 0) {
            return expiry.diff(now, "hours") / 24;
        }
        else {
            const daysDifference = expiry.diff(now, "days");
            return daysDifference;
        }
    };

    const tileWarningColourBackground = () => {

        const days = daysUntilExpiry();

        if (days > 60) {
            // normal
            return "bg-gray-200";
        }

        if (days >= 30 && days <= 60) {
            // Warning
            return "bg-yellow-400";
        }

        if (days < 30) {
            // Danger
            return "bg-red-400";
        }

    };

    return (
        <div className="col-md-3 mt-10px">
            <div style={{ borderRadius: "10px" }} className={`card border-0 text-truncate ${tileWarningColourBackground()}`} >
                <div className="card-body" >
                    <div className="mb-15px">
                        <DocPreview image={doc} />
                    </div>
                    <DocTypeSelector
                        docTypes={docTypes}
                        selectedDocType={getDocType(state.documentType)}
                        onDocTypeSelect={newValue => { handleStateUpdates(v => { v.documentType = getDocType(newValue); return v; }) }}
                    />
                    <SmartInput
                        className={"mt-1 w-100"}
                        label={`${!state.documentType.description ? "Enter" : ""} Document Type Description`}
                        text={state.documentType.description}
                        readOnly={true}
                    />
                    <SmartInput
                        className={"mt-1 w-100"}
                        label={`${!state.title ? "Enter" : ""} Document Name`}
                        text={state.title}
                        updateCallback={newValue => { handleStateUpdates(v => { v.title = newValue; return v; }) }}
                        required={true}
                        validatorFn={() => { return notNullOrEmpty(state.title); }}
                    />
                    <SmartInput
                        className={"mt-1 w-100"}
                        label={`${!state.description ? "Enter" : ""} Document Specific Description`}
                        text={state.description}
                        updateCallback={newValue => { handleStateUpdates(v => { v.description = newValue; return v; }) }}
                        required={true}
                        validatorFn={() => { return notNullOrEmpty(state.description); }}
                    />

                    <DateTimePicker
                        className={"mt-10px w-100"}
                        fetchComplete={true}
                        openDtp={openDtp}
                        showTime={false}
                        dateValue={dateTimeHelper.convertDateStringToMoment(state.expiry)}
                        placeholder="Select Expiry Date"
                        stopEditDate={() => setOpenDtp(false)}
                        setDate={newValue => { handleStateUpdates(v => { v.expiry = convertDateFromMomentToISO(newValue); return v; }) }}
                        editDate={() => setOpenDtp(true)}
                    />

                    {daysUntilExpiry() > 0 && daysUntilExpiry() <= 60 &&
                        <div className="mt-1 fw-bolder">
                            {`${daysUntilExpiry()} days until expiry`}
                        </div>
                    }

                    {daysUntilExpiry() === 0 &&
                        <div className="mt-1 fw-bolder">
                            Expires today
                        </div>
                    }

                    {daysUntilExpiry() < 0 &&
                        <div className="mt-1 fw-bolder">
                            Expired
                        </div>
                    }


                    {showDelete &&
                        <RightAlignedButtons>
                            <div className="form-check form-check-inline">
                                <input className="form-check-input" type="checkbox" id={`delete-${state.$id}`} checked={state.shouldDelete} onChange={() => { handleStateUpdates(v => { if (v.shouldDelete) { delete v.shouldDelete } else { v.shouldDelete = true }; return v; }) }} />
                                <label className="form-check-label text-black" htmlFor={`delete-${state.$id}`}>Delete</label>
                            </div>
                        </RightAlignedButtons>
                    }

                    <RightAlignedButtons>
                        {hasChanges() && <>
                            <button disabled={isSaving} title='Cancel' className="btn btn-warning me-10px" onClick={handleCancel}>
                                <i title='Cancel' className="fa fa-cancel"></i>&nbsp;&nbsp;Cancel
                            </button>
                        </>}
                    </RightAlignedButtons>
                </div>
            </div>
        </div>
    );
});